import React, { useState, useEffect } from 'react';
import { orderAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import Navigation from '../components/layout/Navigation';
import { Package, Clock, CheckCircle, XCircle } from 'lucide-react';

const Dashboard = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await orderAPI.getAll();
      setOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const statusIcons = {
    pending: <Clock size={20} className="text-yellow-500" />,
    processing: <Package size={20} className="text-blue-500" />,
    completed: <CheckCircle size={20} className="text-green-500" />,
    cancelled: <XCircle size={20} className="text-red-500" />
  };

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-brand-bg pt-24 px-6 pb-12">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-outfit font-light tracking-tight text-white mb-2">My Dashboard</h1>
            <p className="text-zinc-400">Welcome back, {user?.name}!</p>
          </div>

          <div className="bg-brand-surface border border-brand-border rounded-xl p-6 mb-8">
            <h2 className="text-xl font-outfit font-semibold text-white mb-4">Profile</h2>
            <div className="space-y-2">
              <div className="flex items-center space-x-3">
                {user?.picture && <img src={user.picture} alt={user.name} className="w-12 h-12 rounded-full" />}
                <div>
                  <p className="text-white font-medium">{user?.name}</p>
                  <p className="text-sm text-zinc-400">{user?.email}</p>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-2xl font-outfit font-semibold text-white mb-6">Order History</h2>
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-violet-600 mx-auto"></div>
              </div>
            ) : orders.length === 0 ? (
              <div className="bg-brand-surface border border-brand-border rounded-xl p-12 text-center">
                <p className="text-zinc-400 mb-4">No orders yet</p>
                <a href="/shop" className="inline-block px-6 py-3 bg-violet-600 text-white rounded-md font-medium hover:bg-violet-500 transition-all">
                  Start Shopping
                </a>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map((order) => (
                  <div key={order.order_id} className="bg-brand-surface border border-brand-border rounded-xl p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <div className="flex items-center space-x-2 mb-2">
                          {statusIcons[order.status]}
                          <span className="text-white font-medium capitalize">{order.status}</span>
                        </div>
                        <p className="text-sm text-zinc-400">Order #{order.order_id.substring(0, 8)}</p>
                        <p className="text-xs text-zinc-500">{new Date(order.created_at).toLocaleDateString()}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-violet-600">{order.total_amount.toFixed(2)}€</p>
                        <p className="text-xs text-zinc-400 capitalize">Payment: {order.payment_status}</p>
                      </div>
                    </div>
                    <div className="border-t border-zinc-800 pt-4">
                      <p className="text-sm text-zinc-400 mb-2">Items:</p>
                      <div className="space-y-1">
                        {order.items.map((item, idx) => (
                          <div key={idx} className="flex justify-between text-sm">
                            <span className="text-zinc-300">{item.product_name} x{item.quantity}</span>
                            <span className="text-white">{(item.price * item.quantity).toFixed(2)}€</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Dashboard;